import React from 'react';

/**
 * FY27 FA1-PSR and FA2-CSR summary-page layout. Counts in the eligibility
 * category table describe the reviewed sample; the final error rate is
 * projected to each grant's HSES funded enrollment and adjusted for allowances.
 */
const GrantHeaders = ({ data, showPercent, showCount = true }) => (
  <thead>
    <tr>
      <th scope="col" rowSpan="2">
        Criteria
      </th>
      {data.map(item => (
        <th
          key={item.granteeId}
          scope="colgroup"
          colSpan={Number(showCount) + Number(showPercent)}
        >
          Grant No. {item.granteeId}
        </th>
      ))}
    </tr>
    <tr>
      {data.map(item => (
        <React.Fragment key={item.granteeId}>
          {showCount && <th scope="col">Count</th>}
          {showPercent && <th scope="col">Percentage</th>}
        </React.Fragment>
      ))}
    </tr>
  </thead>
);

const RAW_CATEGORIES = [
  [
    'Family income is between 100–130% of the poverty guidelines',
    'total100130Count',
  ],
  ['Family income is above 130% of the poverty guidelines', 'totalGT130Count'],
  ['No eligible category determination made by program', 'totalNoDetermCount'],
];

export const Fy27OverallTable = ({ data }) => (
  <>
    <h1>OVERALL ELIGIBILITY FILE SUBMISSION RESULTS</h1>
    <div className="App" id="childFileOutcome">
      <table className="table table-hover table-review">
        <thead>
          <tr>
            <th scope="col" rowSpan="2">
              Criteria
            </th>
            {data.map(item => (
              <th key={item.granteeId} scope="colgroup" colSpan="2">
                Grant No. {item.granteeId}
              </th>
            ))}
          </tr>
          <tr>
            {data.map(item => (
              <React.Fragment key={item.granteeId}>
                <th scope="col">Outcome (Yes/No)</th>
                <th scope="col">Outcome Details (Child file IDs)</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">Are any child files unavailable for review?</th>
            {data.map(item => (
              <React.Fragment key={item.granteeId}>
                <td>{item.noReviewedFilesCount > 0 ? 'Yes' : 'No'}</td>
                <td>{(item.noReviewedFiles || []).join(', ')}</td>
              </React.Fragment>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  </>
);

export const Fy27DocumentationTable = ({ data }) => (
  <>
    <h1>
      ELIGIBILITY FILE REVIEW RESULTS FOR ERSEA DOCUMENTATION - 1302.12(k)
    </h1>
    <div className="App" id="childFileOutcome">
      <table className="table table-hover table-review">
        <GrantHeaders data={data} showPercent />
        <tbody>
          <tr>
            <th scope="row">Total # of files and % of sample reviewed</th>
            {data.map(item => (
              <React.Fragment key={item.granteeId}>
                <td>{item.totalFileReviewed?.count ?? '—'}</td>
                <td>{item.totalFileReviewed?.percent ?? '—'}</td>
              </React.Fragment>
            ))}
          </tr>
          <tr>
            <th scope="row">
              Total # and % of files with any of the documentation errors:
              <ul>
                <li>No eligibility determination record (EDR) for child</li>
                <li>Eligibility category not indicated in file</li>
                <li>
                  The recipient determination does not match reviewer
                  redetermination
                </li>
              </ul>
            </th>
            {data.map(item => (
              <React.Fragment key={item.granteeId}>
                <td>{item.fileErrors?.count ?? '—'}</td>
                <td>{item.fileErrors?.percent ?? '—'}</td>
              </React.Fragment>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  </>
);

export const Fy27EligibilityTable = ({ data }) => (
  <>
    <h1>
      ELIGIBILITY FILE REVIEW RESULTS FOR ERSEA ELIGIBILITY - 1302.12(c)(1)
    </h1>
    <div className="App" id="childFileOutcome">
      <h2>
        Raw count of files in each eligibility category before adjusting for
        allowances
      </h2>
      <table className="table table-hover table-review">
        <GrantHeaders data={data} showPercent={false} />
        <tbody>
          {RAW_CATEGORIES.map(([label, field]) => (
            <tr key={field}>
              <th scope="row">{label}</th>
              {data.map(item => (
                <td key={item.granteeId}>{item[field] ?? '—'}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <h2>
        % of files in error (over allowance) (based on funded enrollment – see
        NOTE below):
      </h2>
      <table className="table table-hover table-review">
        {/* The error count is an intermediate projection, not a reviewed-file count.
            Present only the final rate for each grant in this table. */}
        <GrantHeaders data={data} showPercent showCount={false} />
        <tbody>
          <tr>
            <th scope="row">
              NOTE: The eligibility error rate is calculated using the
              OHS-approved methodology. A direct percentage calculation using
              the raw counts above will not reconcile to the eligibility error
              rate. The eligibility error rate is based on:
              <ol>
                <li>
                  Programs’ ability to roll the 35% allowance into the 10%
                  allowance, where the 10% allowance has not been fully used;
                  and
                </li>
                <li>
                  Projecting the results from the sample of files reviewed to
                  calculate error based on the program’s funded enrollment for
                  each grant
                </li>
              </ol>
            </th>
            {data.map(item => (
              <td key={item.granteeId}>{item.fileErrors?.percent ?? '—'}</td>
            ))}
          </tr>
        </tbody>
      </table>
      {/* <p>
        Note: The eligibility error rate uses the OHS-approved methodology. Raw
        category counts are from reviewed files; each category is projected to
        the grant’s funded enrollment before applying category allowances. A
        percentage calculated directly from the raw counts will not generally
        equal the projected error rate. When no files were reviewed, the rate is
        reported as “NO FILES REVIEWED.”
      </p> */}
    </div>
  </>
);
